// src/pages/news/NewsPost.jsx
import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring, useReducedMotion } from "framer-motion";
import newsData from "../../data/newsData";

const fadeUp = {
  hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.5, ease: "easeOut" } },
};

export default function NewsPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  // Find post + index (always called)
  const { post, index } = useMemo(() => {
    const i = newsData.findIndex((item) => item.slug === slug);
    return { post: i >= 0 ? newsData[i] : null, index: i };
  }, [slug]);

  // If slug invalid, go back to listing
  useEffect(() => {
    if (!post) navigate("/news", { replace: true });
  }, [post, navigate]);

  // SEO/title
  useEffect(() => {
    if (post?.title) document.title = `${post.title} | Glide Education`;
  }, [post]);

  // Read time (≈200 wpm)
  const readMins = useMemo(() => {
    const text = post?.content ? String(post.content) : "";
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    return Math.max(1, Math.ceil(words / 200));
  }, [post]);

  // Share helpers
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const [toast, setToast] = useState("");
  const share = useCallback(async () => {
    try {
      if (navigator.share && post) {
        await navigator.share({
          title: post.title,
          text: post.excerpt || post.title,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setToast("Link copied to clipboard");
        setTimeout(() => setToast(""), 2000);
      }
    } catch {}
  }, [post, shareUrl]);

  // Prev/Next (no hooks)
  const prev = index > 0 ? newsData[index - 1] : null;
  const next = index < newsData.length - 1 ? newsData[index + 1] : null;

  // Related posts by tag (always called)
  const related = useMemo(() => {
    if (!post?.tags) return [];
    const set = new Set(post.tags);
    return newsData
      .filter((p) => p.slug !== post.slug && p.tags?.some((t) => set.has(t)))
      .slice(0, 3);
  }, [post]);

  // Scroll progress hooks (always called)
  const scrollRef = useRef(null);
  const { scrollYProgress } = useScroll({ container: scrollRef, layoutEffect: false });
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 20, restDelta: 0.001 });

  // Early return AFTER all hooks are declared
  if (!post) return null;

  return (
    <div className="relative">
      {/* bg */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-blue-50 via-white to-white" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-200/40 blur-3xl" />

      {/* progress bar */}
      <motion.div
        style={{ scaleX: progress, transformOrigin: "0% 50%" }}
        className="fixed left-0 top-0 z-[60] h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500"
      />

      {/* toast */}
      {toast && (
        <div className="fixed left-1/2 top-16 z-[60] -translate-x-1/2 rounded-full bg-gray-900/90 px-4 py-2 text-sm text-white shadow">
          {toast}
        </div>
      )}

      <div ref={scrollRef} className="mx-auto max-w-3xl px-6 pt-24 pb-16">
        <Link to="/news" className="inline-flex items-center gap-2 text-blue-600 hover:underline">
          ← Back to News
        </Link>

        {/* Title + meta */}
        <motion.header
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-4"
        >
          <h1 className="text-3xl font-extrabold tracking-tight text-blue-800">{post.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <span>{post.date}</span>
            <span>•</span>
            <span>{readMins} min read</span>
            {Array.isArray(post.tags) && post.tags.length > 0 && (
              <>
                <span>•</span>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/news`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/news#${encodeURIComponent(t)}`;
                      }}
                      className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700 ring-1 ring-blue-100 hover:bg-blue-100"
                      title={`View more in ${t}`}
                    >
                      #{t}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </motion.header>

        {/* Cover image */}
        {post.image && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.45 }}
            className="mt-5 overflow-hidden rounded-2xl ring-1 ring-gray-200"
          >
            <div className="relative aspect-[16/9] w-full">
              <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
              <img
                src={post.image}
                alt={post.title}
                loading="lazy"
                onLoad={(e) => (e.currentTarget.previousSibling.style.display = "none")}
                className={`absolute inset-0 h-full w-full object-cover ${
                  prefersReducedMotion ? "" : "transition duration-700"
                } `}
              />
            </div>
          </motion.div>
        )}

        {/* Content */}
        <motion.article
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="prose prose-blue mt-6 max-w-none text-gray-800 prose-p:leading-relaxed"
        >
          <div className="whitespace-pre-line">{post.content}</div>
        </motion.article>

        {/* Share */}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button
            onClick={share}
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-xl bg-blue-600 px-4 py-2 font-semibold text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200"
          >
            <span className="absolute inset-0 -z-10 translate-x-[-100%] bg-white/20 blur-sm transition group-hover:translate-x-[100%]" />
            Share / Copy link
          </button>
          <a href={shareUrl} className="text-sm text-blue-600 hover:underline" target="_blank" rel="noreferrer">
            Open in new tab
          </a>
        </div>

        {/* Prev/Next */}
        <div className="mt-10 border-t pt-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              {prev ? (
                <Link
                  to={`/news/${prev.slug}`}
                  className="group flex items-center gap-3 rounded-xl ring-1 ring-gray-200 bg-white p-3 hover:ring-blue-200 hover:shadow-sm transition"
                  title={prev.title}
                >
                  {prev.image ? (
                    <img src={prev.image} alt={prev.title} className="h-16 w-24 flex-none rounded-lg object-cover" loading="lazy" />
                  ) : (
                    <div className="h-16 w-24 flex-none rounded-lg bg-gray-100" />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">← Previous</p>
                    <p className="truncate font-semibold text-gray-800 group-hover:text-blue-700">{prev.title}</p>
                  </div>
                </Link>
              ) : (
                <div className="rounded-xl ring-1 ring-gray-100 bg-gray-50 p-3 text-gray-400">← Previous</div>
              )}
            </div>
            <div className="sm:text-right">
              {next ? (
                <Link
                  to={`/news/${next.slug}`}
                  className="group flex items-center gap-3 rounded-xl ring-1 ring-gray-200 bg-white p-3 hover:ring-blue-200 hover:shadow-sm transition sm:flex-row-reverse"
                  title={next.title}
                >
                  {next.image ? (
                    <img src={next.image} alt={next.title} className="h-16 w-24 flex-none rounded-lg object-cover" loading="lazy" />
                  ) : (
                    <div className="h-16 w-24 flex-none rounded-lg bg-gray-100" />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Next →</p>
                    <p className="truncate font-semibold text-gray-800 group-hover:text-blue-700">{next.title}</p>
                  </div>
                </Link>
              ) : (
                <div className="rounded-xl ring-1 ring-gray-100 bg-gray-50 p-3 text-gray-400 sm:ml-auto sm:max-w-[60%]">Next →</div>
              )}
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-blue-700 mb-4">Related</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/news/${r.slug}`}
                  className="group overflow-hidden rounded-2xl ring-1 ring-gray-200 bg-white hover:ring-blue-200 hover:shadow-sm transition"
                >
                  <div className="relative h-40 w-full overflow-hidden">
                    {r.image ? (
                      <>
                        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
                        <img
                          src={r.image}
                          alt={r.title}
                          loading="lazy"
                          onLoad={(e) => (e.currentTarget.previousSibling.style.display = "none")}
                          className={`h-full w-full object-cover ${prefersReducedMotion ? "" : "transition duration-500 group-hover:scale-[1.03]"}`}
                        />
                      </>
                    ) : (
                      <div className="h-full w-full bg-gray-100" />
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-gray-500">{r.date}</span>
                    <h3 className="mt-1 line-clamp-2 font-semibold text-gray-900 group-hover:text-blue-700">{r.title}</h3>
                    {r.tags && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {r.tags.slice(0, 3).map((t, i) => (
                          <span key={i} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 ring-1 ring-gray-200">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
